import { create } from "zustand";

export interface DynamicFlag {
  key: string;
  label: string;
  description: string;
  envValue: boolean;
}

export const DYNAMIC_FLAGS: DynamicFlag[] = [
  {
    key: "VITE_FF_SETTINGS_PAGE_GENERAL",
    label: "Settings page — User Management",
    description:
      "Show the User Management card on the General settings tab. Off by default: the card is hidden until the feature is production-ready.",
    envValue: import.meta.env.VITE_FF_SETTINGS_PAGE_GENERAL === "true",
  },
  {
    key: "VITE_FF_SETTINGS_PAGE_TEXT",
    label: "Settings page — Text 10DLC registration",
    description:
      "Show the 10DLC company info / registration card on the Text settings tab. Off by default: the card is hidden until the feature is production-ready.",
    envValue: import.meta.env.VITE_FF_SETTINGS_PAGE_TEXT === "true",
  },
  {
    key: "VITE_FF_SETTINGS_PAGE_BILLING",
    label: "Settings page — Billing",
    description:
      "Show the Billing tab and route in workspace Settings. Off by default until the payment method dialog is ready for go-live.",
    envValue: import.meta.env.VITE_FF_SETTINGS_PAGE_BILLING === "true",
  },
  {
    key: "VITE_FF_BRAND_BOOK_EXCHANGES_PREVIEW",
    label: "Brand Book — Exchanges preview",
    description:
      "Show the Emails / Exchanges toggle on the Brand Book Brand Style step, previewing curated Exchange blueprints in the live brand.",
    envValue: import.meta.env.VITE_FF_BRAND_BOOK_EXCHANGES_PREVIEW === "true",
  },
  {
    key: "VITE_FF_SMS_REGISTRATION",
    label: "Settings page — SMS registration",
    description:
      "Show the staff-only SMS registration tab in workspace Settings, where staff register a site with InfoBip from the merchant's Typeform response. Off by default.",
    envValue: import.meta.env.VITE_FF_SMS_REGISTRATION === "true",
  },
  {
    key: "VITE_FF_BROADCAST_V2_MAINTENANCE",
    label: "BroadcastV2 maintenance",
    description:
      "Block all BroadcastV2 pages behind a maintenance window instead of rendering the feature.",
    envValue: import.meta.env.VITE_FF_BROADCAST_V2_MAINTENANCE === "true",
  },
  {
    key: "VITE_FF_ENABLE_SMART_SEND",
    label: "Smart Send",
    description:
      "Enable the Smart Send scheduling option in BroadcastV2 compose.",
    envValue: import.meta.env.VITE_FF_ENABLE_SMART_SEND === "true",
  },
  {
    key: "VITE_FF_ENABLE_EMAIL_FREQUENCY_CAP_OVERRIDE",
    label: "Email frequency cap override",
    description:
      "Show the Override Frequency Caps toggle on email broadcasts in BroadcastV2 compose.",
    envValue:
      import.meta.env.VITE_FF_ENABLE_EMAIL_FREQUENCY_CAP_OVERRIDE === "true",
  },
  {
    key: "VITE_FF_MAINTENANCE_BANNER",
    label: "Maintenance banner",
    description:
      "Show a global maintenance warning banner above the nav for selected users.",
    envValue: import.meta.env.VITE_FF_MAINTENANCE_BANNER === "true",
  },
  {
    key: "VITE_FF_MAINTENANCE_BANNER_REGULAR_USERS_ONLY",
    label: "Maintenance banner — regular users only",
    description:
      "When on, hide the banner from staff. When off, all authenticated users see it.",
    envValue:
      import.meta.env.VITE_FF_MAINTENANCE_BANNER_REGULAR_USERS_ONLY !== "false",
  },
  {
    key: "VITE_FF_MAINTENANCE_MODE",
    label: "Maintenance mode",
    description:
      "Block logged-in regular users (role=user) behind a maintenance screen. Staff and login remain available.",
    envValue: import.meta.env.VITE_FF_MAINTENANCE_MODE === "true",
  },
  {
    key: "VITE_FF_ENABLE_SKU_STORE_STORIES",
    label: "SKU Store Stories",
    description:
      "Show the Stories tab and routes in the SKU Store product detail view.",
    envValue: import.meta.env.VITE_FF_ENABLE_SKU_STORE_STORIES === "true",
  },
  {
    key: "VITE_FF_ENABLE_GENLAB",
    label: "GenLab",
    description:
      "Kill-switch only — this toggle can DISABLE GenLab where the env var already enables it (staging), but cannot enable it where the env var is off (production). Hides the GenLab nav entry and blocks the /genlab/:siteId routes. Off in production: GenLab is still in development against the creativesphere backend.",
    envValue: import.meta.env.VITE_FF_ENABLE_GENLAB === "true",
  },
  {
    key: "VITE_FF_ENABLE_GENERATIONS",
    label: "Generations",
    description:
      "Kill-switch only — this toggle can DISABLE Generations where the env var already enables it (staging), but cannot enable it where the env var is off (production). Hides the Generations nav entry and blocks the /generations/:siteId routes.",
    envValue: import.meta.env.VITE_FF_ENABLE_GENERATIONS === "true",
  },
  {
    key: "VITE_FF_TEXT_COMPOSER_RCS_RICH",
    label: "Text composer — RCS Rich",
    description:
      "Raise the RCS body limit to 3,072 chars and show suggested-action pills. Env var is `true` for all sites or a per-site allowlist in _SITES; this toggle force-enables it for every site in your session.",
    envValue: import.meta.env.VITE_FF_TEXT_COMPOSER_RCS_RICH === "true",
  },
  {
    key: "VITE_FF_ENABLE_AUDIENCE_ESTIMATES",
    label: "Audience builder estimates",
    description:
      "Show per-filter match counts and the overall estimated size while building an audience. Each uncached filter costs a source query, so this is the kill switch for that spend: turning it off here stops it for every site, allowlisted ones included.",
    envValue: import.meta.env.VITE_FF_ENABLE_AUDIENCE_ESTIMATES === "true",
  },
  {
    key: "VITE_FF_SHOW_WHEN_CONNECTORS",
    label: "Show when — connector fields",
    description:
      "Offer connected-app fields (Yotpo points, tier…) in the email editor's Show when condition. Off leaves Cart as the only source; VITE_FF_SHOW_WHEN_CONNECTORS_SITES opts individual sites in without turning it on everywhere.",
    envValue: import.meta.env.VITE_FF_SHOW_WHEN_CONNECTORS === "true",
  },
  {
    key: "VITE_FF_NOTIFICATIONS",
    label: "Notifications",
    description:
      "Show the notifications bell and panel. Off by default; VITE_FF_NOTIFICATIONS_SITES opts individual sites in without turning it on everywhere.",
    envValue: import.meta.env.VITE_FF_NOTIFICATIONS === "true",
  },
  {
    key: "VITE_FF_APPLE_MESSAGES_CONNECT",
    label: "Apple Messages connect page",
    description:
      "Enable the /apple-messages/connect page that Apple Business Register sends brands to. Off by default; while off, the page redirects home.",
    envValue: import.meta.env.VITE_FF_APPLE_MESSAGES_CONNECT === "true",
  },
];

export const isDynamicFeatureFlagsEnabled = DYNAMIC_FLAGS.length > 0;

const STORAGE_KEY = "connect-ui:feature-flag-overrides";

type Overrides = Partial<Record<string, boolean>>;

const DYNAMIC_FLAG_KEYS = new Set(DYNAMIC_FLAGS.map((flag) => flag.key));

function readInitial(): Overrides {
  if (!isDynamicFeatureFlagsEnabled) return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Overrides;
    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([key, value]) =>
          DYNAMIC_FLAG_KEYS.has(key) && typeof value === "boolean",
      ),
    );
  } catch {
    return {};
  }
}

function persist(overrides: Overrides) {
  if (!isDynamicFeatureFlagsEnabled) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    /* ignore quota / disabled storage */
  }
}

interface FeatureFlagOverrideStore {
  overrides: Overrides;
  setOverride: (key: string, enabled: boolean) => void;
  clearOverride: (key: string) => void;
}

export const useFeatureFlagOverrideStore = create<FeatureFlagOverrideStore>(
  (set) => ({
    overrides: readInitial(),
    setOverride: (key, enabled) =>
      set((state) => {
        const overrides = { ...state.overrides, [key]: enabled };
        persist(overrides);
        return { overrides };
      }),
    clearOverride: (key) =>
      set((state) => {
        const overrides = { ...state.overrides };
        delete overrides[key];
        persist(overrides);
        return { overrides };
      }),
  }),
);

export function resolveDynamicFlag(key: string, envValue: boolean): boolean {
  if (!isDynamicFeatureFlagsEnabled) return envValue;
  return useFeatureFlagOverrideStore.getState().overrides[key] ?? envValue;
}

export function useDynamicFlag(key: string, envValue: boolean): boolean {
  const override = useFeatureFlagOverrideStore((s) => s.overrides[key]);
  if (!isDynamicFeatureFlagsEnabled) return envValue;
  return override ?? envValue;
}
